import { Injectable } from '@angular/core';
import { DateService } from './date.service';
import { CredentialsService } from './credentials.service';
import { BroadcastService } from './broadcast.service';
import { BroadcastEvents } from '../enums/broadcast-events';
import { SelectedBurialsTab } from '../enums/selected-burials-tab';

declare function md5(string: string): string;
declare function clearQRCode(): boolean;
declare function makeQRCode(qrCodeString: string): boolean;
declare function convenientSaveModalDataToDrafts(): void;
declare function handleTempModalData(tempModalData: any): any;

@Injectable({
  providedIn: 'root'
})
export class ModalService {

  constructor(private dateService: DateService,
    private credentialsService: CredentialsService,
    private broadcastService: BroadcastService) { }

  modalDataObject: any;
  newQR: boolean = false;
  newFilledFields: any;
  shouldBeReadOnly: boolean = false;
  selectedTab?: SelectedBurialsTab;

  updateQRCode() {
    var qrString = this.dateService.getTimeStamp();

    if (this.modalDataObject.burialData.inputName) {
      qrString += this.modalDataObject.burialData.inputName;
    }

    if (this.modalDataObject.burialData.inputLastName) {
      qrString += this.modalDataObject.burialData.inputLastName;
    }

    if (this.newQR || !this.modalDataObject.burialData.qrstring) {
      this.newQR = true;
      this.modalDataObject.burialData.qrstring = md5(qrString);
    }

    clearQRCode();
    makeQRCode(this.modalDataObject.burialData.qrstring);
  }

  loadDataObjectsFromDrafts(): any {
    var currentDrafts = null;

    const drafts = localStorage.getItem('drafts');
    if (drafts) {
      currentDrafts = JSON.parse(drafts);
    }

    if (!currentDrafts) {
      currentDrafts = [];
    }

    return currentDrafts;
  }

  deleteDataObjectFromDrafts(dataObject: any) {
    const drafts = localStorage.getItem('drafts');

    if (!dataObject || !dataObject.burialData || !dataObject.burialData.qrstring || !drafts) { return; }

    var shouldUpdateData = false;

    var currentDrafts = JSON.parse(drafts);
    for (var draftIndex in currentDrafts) {
      if (currentDrafts[draftIndex].burialData.qrstring == dataObject.burialData.qrstring) {
        currentDrafts.splice(draftIndex, 1);
        shouldUpdateData = true;
        break;
      }
    }

    if (shouldUpdateData) {
      localStorage.setItem('drafts', JSON.stringify(currentDrafts));
    }
  }

  saveCurrentDataObjectToDrafts() {
    convenientSaveModalDataToDrafts();
  }

  saveDataObjectAsDraft(dataObject: any): { success: boolean; message: string; oldestDeleted?: boolean } {
    try {
      // Validierung
      if (!dataObject || !dataObject.burialData) {
        return { success: false, message: 'Ungültige Daten' };
      }

      if (!dataObject.burialData.inputName || !dataObject.burialData.inputLastName) {
        return { success: false, message: 'Bitte Vorname und Nachname angeben' };
      }

      if (!dataObject.burialData.qrstring) {
        return { success: false, message: 'QR-String fehlt' };
      }

      // Lade bestehende Entwürfe
      var currentDrafts = this.loadDataObjectsFromDrafts();
      var isUpdate = false;
      var oldestDeleted = false;

      // Prüfe, ob bereits ein Entwurf mit gleichem qrstring existiert
      for (var draftIndex in currentDrafts) {
        if (currentDrafts[draftIndex].burialData && 
            currentDrafts[draftIndex].burialData.qrstring === dataObject.burialData.qrstring) {
          // Update bestehenden Entwurf
          dataObject.isDraft = true;
          dataObject.updatedAt = Date.now();
          // createdAt beibehalten, falls vorhanden
          if (currentDrafts[draftIndex].createdAt) {
            dataObject.createdAt = currentDrafts[draftIndex].createdAt;
          } else {
            dataObject.createdAt = Date.now();
          }
          currentDrafts[draftIndex] = dataObject;
          isUpdate = true;
          break;
        }
      }

      // Wenn kein Update, füge neuen Entwurf hinzu
      if (!isUpdate) {
        dataObject.isDraft = true;
        dataObject.createdAt = Date.now();

        // Prüfe, ob bereits 5 Entwürfe vorhanden sind
        if (currentDrafts.length >= 5) {
          // Finde den ältesten Entwurf (niedrigster createdAt Wert)
          var oldestIndex = 0;
          var oldestTimestamp = currentDrafts[0].createdAt || 0;
          
          for (var i = 1; i < currentDrafts.length; i++) {
            var draftTimestamp = currentDrafts[i].createdAt || 0;
            if (draftTimestamp < oldestTimestamp) {
              oldestTimestamp = draftTimestamp;
              oldestIndex = i;
            }
          }

          // Lösche den ältesten Entwurf
          currentDrafts.splice(oldestIndex, 1);
          oldestDeleted = true;
        }

        currentDrafts.push(dataObject);
      }

      // Speichere in localStorage
      localStorage.setItem('drafts', JSON.stringify(currentDrafts));

      return { 
        success: true, 
        message: isUpdate ? 'Entwurf aktualisiert' : 'Entwurf gespeichert',
        oldestDeleted: oldestDeleted
      };
    } catch (error) {
      console.error('Fehler beim Speichern des Entwurfs:', error);
      return { success: false, message: 'Entwurf konnte nicht gespeichert werden' };
    }
  }

  initNewBurialModal(burialDataForEdit?: any) {
    this.credentialsService.fullName = '';
    this.credentialsService.fullLastName = '';
    this.newQR = false;

    this.modalDataObject = {};
    this.newFilledFields = [];

    if (burialDataForEdit) {
      this.modalDataObject = burialDataForEdit;
      this.credentialsService.fullLastName = burialDataForEdit.burialData.inputLastName;
      this.credentialsService.fullName = burialDataForEdit.burialData.inputName;
      this.shouldBeReadOnly = !(burialDataForEdit.hasOwnProperty('isDraft') && burialDataForEdit.isDraft);
    } else {
      this.shouldBeReadOnly = false;
    }

    if (!this.modalDataObject.burialData) {
      this.modalDataObject.burialData = {};
    }

    if (!this.modalDataObject.burialDates) {
      this.modalDataObject.burialDates = {};
    }

    if (!this.modalDataObject.burialDocs) {
      this.modalDataObject.burialDocs = {};
    }
  }

  initBurialInfoModal(selectedBurial: any) {
    this.modalDataObject = {};
    this.newFilledFields = [];
    this.modalDataObject = selectedBurial;
    this.shouldBeReadOnly = true;

    if (!this.modalDataObject.hasOwnProperty('burialData')) {
      this.modalDataObject.burialData = {};
    }

    if (selectedBurial && selectedBurial.hasOwnProperty('v_qrstring')) {
      this.modalDataObject.burialData.qrstring = selectedBurial.v_qrstring;
    }

    if (selectedBurial && selectedBurial.hasOwnProperty('fid')) {
      this.modalDataObject.burialData.auftrag_id = selectedBurial.fid;
    }

    // Verzögerter Dispatch, damit Angular die Modal-Komponenten rendern kann
    setTimeout(() => this.broadcastService.dispatch(BroadcastEvents.reloadData), 0);
  }

  setModalData(key: string, value: any) {
    this.modalDataObject[key] = value;
    if (this.newQR) { handleTempModalData(this.modalDataObject); }
  }

  getModalData(onlyNewFields?: boolean): any {
    if (!onlyNewFields) { return this.modalDataObject; }

    var modalDataProjection: any = {};
    if (!this.modalDataObject.hasOwnProperty('burialData') || !this.modalDataObject.burialData.hasOwnProperty('qrstring')) { return this.modalDataObject; }

    if (!modalDataProjection.hasOwnProperty('burialData')) {
      modalDataProjection['burialData'] = {};
    }

    modalDataProjection.burialData.qrstring = this.modalDataObject.burialData.qrstring;
    for (var subCategory in this.modalDataObject) {
      for (var subSubCategory in this.modalDataObject[subCategory]) {
        for (var newFieldIndex in this.newFilledFields) {
          var newField = this.newFilledFields[newFieldIndex];
          if (newField == subSubCategory) {
            if (!modalDataProjection.hasOwnProperty(subCategory)) {
              modalDataProjection[subCategory] = {};
            }
            modalDataProjection[subCategory][subSubCategory] = this.modalDataObject[subCategory][subSubCategory];
          }
        }
      }
    }

    return modalDataProjection;
  }

  inputNameDidChange(newValue: string) {
    this.credentialsService.fullName = newValue;
    this.updateQRCode();
  }

  inputLastNameDidChange(newValue: string) {
    this.credentialsService.fullLastName = newValue;
    this.updateQRCode();
  };

  currentDeathType(): number {
    var responseState = 0;
    if (this.modalDataObject.hasOwnProperty('burialData') && this.modalDataObject.burialData.hasOwnProperty('inputDeathType') && this.modalDataObject.burialData.inputDeathType) {
      responseState = this.modalDataObject.burialData.inputDeathType;
    }
    return responseState;
  }

  didInputForClass(inputClass: any) {
    this.newFilledFields.push(inputClass);
  };

}
